import React, { Fragment, ReactNode, forwardRef, useContext, useMemo } from 'react';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { WithTranslation } from 'react-i18next';
import getNodeText from 'ia-react-core/~/utils/components/#getNodeText';
import DropList from '../#DropList';

import {
  StyledDropdownButton,
  StyledDropdownText,
  StyledEmptyDropdownText,
  StyledChevron,
} from './Dropdown.styles';
import FormColContext from '../#FormColContext';
import { DropListOption, DropListProps } from '../#DropList/DropList';

export type DropdownOptions<Value> = DropListOption<Value>[];

export interface DropdownProps<Value, OptionValue> extends
  Omit<DropListProps<Value, OptionValue>, 'ref' | 'Target'>,
  Omit<SCP<'button'>, 'onKeyDown' | 'onBlur' | 'value'> {
  name?: string;
   /** Array of options. */
  options?: DropListOption<OptionValue>[];
  /** Disables the dropdown. */
  disabled?: boolean;
  /** Displays the dropdowned as read only. */
  readOnly?: boolean;
  /** Displays the dropdowned value only. */
  textOnly?: boolean;
  /** Custom label for the message when the dropdown is empty. */
  emptyLabel?: string;
  /** Value to use when the dropdown is empty. */
  emptyValue?: any;
  noEmptyValue?: boolean;
  offsetFromEdge?: number;
  selectionDelay?: number;
  isUnionControlGroup?: boolean;
}

interface TargetComponentProps<Value> {
  searchValue: Value;
  isEmpty: boolean;
  disabled: boolean;
  readOnly: boolean;
  selectedOptions: DropListOption<Value>[];
  resolvedEmptyLabel: ReactNode;
  isValid: boolean;
  hasWarning: boolean;
  hasCaution: boolean;
  isTouched: boolean;
  isOpened: boolean;
}

const DropdownDefaultProps: Partial<DropdownProps<unknown, unknown>> = {
  options: [],
  offsetFromEdge: 8,
  selectionDelay: 200,
  disabled: false,
  readOnly: false,
  textOnly: false,
  emptyValue: '',
  noEmptyValue: false,
};

/**
 * @mode developer
 */
const Dropdown = <Value, OptionValue>({ t, tReady, value, emptyValue, emptyLabel, isUnionControlGroup, noEmptyValue, ...props }: DropdownProps<Value, OptionValue> & WithTranslation) => {
  const { colId } = useContext(FormColContext);

  const Target = useMemo(() => {
    // eslint-disable-next-line react/no-unstable-nested-components
    const TargetComponent = forwardRef<HTMLButtonElement, TargetComponentProps<Value>>(({
      searchValue, isEmpty, disabled, readOnly, selectedOptions, resolvedEmptyLabel, isValid, hasWarning, hasCaution, isTouched, isOpened, ...p
    }, ref) => (
      <StyledDropdownButton
        type="button"
        data-testid="Dropdown"
        {...p}
        disabled={disabled}
        readOnly={readOnly}
        ref={ref}
        isValid={isValid}
        hasWarning={hasWarning}
        hasCaution={hasCaution}
        isTouched={isTouched}
        isOpened={isOpened}
        isEmpty={isEmpty}
        isUnionControlGroup={isUnionControlGroup}
      >
        {!isEmpty && (
          <StyledDropdownText>
            {selectedOptions.length === 1 && getNodeText(selectedOptions[0]?.label)}
            {selectedOptions.length > 1 && (
              <>
                {selectedOptions.length} {t('selected')}
              </>
            )}
          </StyledDropdownText>
        )}
        {isEmpty && (
          <StyledEmptyDropdownText
            disabled={disabled}
            readOnly={readOnly}
          >
            {resolvedEmptyLabel || t('emptyLabel')}
          </StyledEmptyDropdownText>
        )}
        <StyledChevron
          isOpened={isOpened}
          disabled={disabled || readOnly}
        />
      </StyledDropdownButton>
    ));

    return TargetComponent;
  }, [t, isUnionControlGroup]);

  return (
    <DropList<Value, {}, OptionValue>
      aria-labelledby={colId}
      {...props}
      value={value}
      emptyValue={emptyValue}
      emptyLabel={emptyLabel || t('emptyLabel')}
      noEmptyValue={noEmptyValue}
      Target={Target}
      isUnionControlGroup={isUnionControlGroup}
    />
  );
};

Dropdown.defaultProps = DropdownDefaultProps;

export default Dropdown;
